import React from 'react';
import { useTranslation } from '../i18n';

// Add new versions here (PDF in frontend/public). The newest version already in
// effect is shown as current; versions with a future date are shown as upcoming
// and become current automatically on their effective date.
// Keep in sync with TermsInfo.Versions in backend/Models/TermsInfo.cs, which records the
// version each user accepts at registration.
const TERMS_VERSIONS = [
  { validFrom: '2026-04-20', file: '/terms-cs-2026-04-20.pdf' },
  { validFrom: '2026-11-08', file: '/terms-cs-2026-11-08.pdf' },
  { validFrom: '2026-11-19', file: '/terms-cs-2026-11-19.pdf' },
];

const localDateString = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDate = (iso: string, lang: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(lang === 'cs' ? 'cs-CZ' : 'en-GB');
};

export const TermsPage: React.FC = () => {
  const { t, lang } = useTranslation();

  const today = localDateString(new Date());
  const sorted = [...TERMS_VERSIONS].sort((a, b) => a.validFrom.localeCompare(b.validFrom));
  const effective = sorted.filter((v) => v.validFrom <= today);
  const current = effective[effective.length - 1];
  const upcoming = sorted.filter((v) => v.validFrom > today);

  const renderPdf = (file: string) => (
    <object data={file} type="application/pdf" className="terms-pdf">
      <p>
        <a href={file} target="_blank" rel="noopener noreferrer">
          {t('terms.download')}
        </a>
      </p>
    </object>
  );

  return (
    <div className="terms-page">
      <h2>{t('terms.title')}</h2>
      {current && (
        <section className="terms-version">
          {upcoming.length > 0 && (
            <h3>{t('terms.current', { date: formatDate(current.validFrom, lang) })}</h3>
          )}
          {renderPdf(current.file)}
        </section>
      )}
      {upcoming.map((v) => (
        <section className="terms-version" key={v.validFrom}>
          <h3>{t('terms.upcoming', { date: formatDate(v.validFrom, lang) })}</h3>
          {renderPdf(v.file)}
        </section>
      ))}
    </div>
  );
};
